import { z } from "zod";
import { itemCodeSchema } from "./catalog.js";
import { latLngSchema } from "./geo.js";
/**
 * The order contract between app and server.
 *
 * Two rules make this safe to expose to the internet:
 *   1. The client never sends prices. It sends item codes and quantities, and
 *      the server prices them from ERPNext. Otherwise a customer can edit the
 *      request and buy 2L milk for a cent.
 *   2. Every submit carries an idempotency key, so a flaky 4G connection that
 *      retries cannot create two identical Sales Orders.
 */
/** Cambodian mobile numbers: 0 + 8 or 9 digits, spaces and dashes tolerated. */
export const phoneSchema = z
    .string()
    .trim()
    .transform((s) => s.replace(/[\s-]/g, ""))
    .pipe(z.string().regex(/^0\d{8,9}$/, "Enter a Cambodian mobile number, e.g. 012 345 678"));
export const orderLineSchema = z.object({
    code: itemCodeSchema,
    qty: z.number().int().positive().max(2000),
});
export const draftOrderSchema = z
    .object({
    channel: z.enum(["retail", "wholesale"]),
    customerName: z.string().trim().min(2).max(140),
    phone: phoneSchema,
    address: z.string().trim().min(8).max(500),
    /** Where the driver goes. Required for retail: it sets the fee. */
    location: latLngSchema.optional(),
    note: z.string().trim().max(280).optional(),
    /** Wholesale only: the buyer's own purchase-order reference. */
    poNumber: z.string().trim().max(60).optional(),
    /** Wholesale only: the code the office gives its trade customers. */
    wholesaleCode: z.string().trim().max(60).optional(),
    lines: z.array(orderLineSchema).min(1).max(40),
    /** Client-generated, stable across retries of the same submit. */
    idempotencyKey: z.string().uuid(),
})
    .superRefine((draft, ctx) => {
    if (draft.channel === "retail" && !draft.location) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["location"],
            message: "Drop a pin on the map so we know where to deliver.",
        });
    }
});
export const ORDER_STAGES = [
    "placed",
    "confirmed",
    "packed",
    "on_the_way",
    "delivered",
    "cancelled",
];
/** The ERPNext Sales Order Item shortfall reasons, verbatim, so nothing maps. */
export const SHORTFALL_REASONS = ["ខូច", "ត្រឡប់", "ជិតផុតកំណត់", "មិនយក", "ផ្សេងៗ"];
/** What the driver actually handed over for one line. */
export const deliveredLineSchema = z.object({
    code: itemCodeSchema,
    qty: z.number().int().nonnegative(),
    reason: z.enum(SHORTFALL_REASONS).optional(),
});
export const orderEventSchema = z.object({
    at: z.string().datetime(),
    kind: z.enum(["confirmed", "on_the_way", "delivered", "cancelled", "paid"]),
    by: z.string(),
    note: z.string().optional(),
});
export const orderSchema = z.object({
    id: z.string().min(1),
    /** ERPNext Sales Order name, absent while the bridge is still retrying. */
    erpName: z.string().min(1).optional(),
    /** Something the office should know about the ERPNext booking, e.g. a customer fallback. */
    erpNote: z.string().optional(),
    channel: z.enum(["retail", "wholesale"]).default("retail"),
    /** Who and where. Address and note are stripped from the public view. */
    customer: z
        .object({
        name: z.string(),
        address: z.string().optional(),
        note: z.string().optional(),
    })
        .optional(),
    poNumber: z.string().optional(),
    stage: z.enum(ORDER_STAGES),
    deliveryDate: z.string().date(),
    subtotalCents: z.number().int().nonnegative(),
    deliveryCents: z.number().int().nonnegative(),
    totalCents: z.number().int().nonnegative(),
    /** Old orders carried a zone key; new ones carry a pin. */
    zoneKey: z.string().optional(),
    location: latLngSchema.optional(),
    km: z.number().nonnegative(),
    /** "estimated" when the router was unreachable and km is straight-line times detour. */
    kmSource: z.enum(["routed", "estimated"]).default("routed"),
    lines: z.array(orderLineSchema.extend({
        nameEn: z.string(),
        nameKh: z.string(),
        priceCents: z.number().int().nonnegative(),
    })),
    payment: z
        .object({
        method: z.enum(["khqr", "cash", "invoice"]),
        status: z.enum(["unpaid", "pending", "paid", "failed", "expired"]),
        /** Bakong's hash of the QR, used to poll for settlement. */
        md5: z.string().optional(),
    })
        .optional(),
    createdAt: z.string().datetime(),
    /** Set when the driver marks Delivered; totals are recomputed from it. */
    delivered: z.array(deliveredLineSchema).optional(),
    events: z.array(orderEventSchema).optional(),
});
/** Stages only ever move forwards, apart from cancellation. */
const ORDER_OF_STAGE = {
    placed: 0,
    confirmed: 1,
    packed: 2,
    on_the_way: 3,
    delivered: 4,
    cancelled: 99,
};
export function canAdvance(from, to) {
    if (from === "delivered" || from === "cancelled")
        return false;
    if (to === "cancelled")
        return true;
    return ORDER_OF_STAGE[to] > ORDER_OF_STAGE[from];
}
/** Why a customer pulled an order. Stored as the event note so the office can read it. */
export const CANCEL_REASONS = ["mistake", "wrong_address", "changed_mind", "other"];
/** A customer may cancel until the driver has taken the order out. After that, they call. */
export function canCustomerCancel(stage) {
    return stage === "placed" || stage === "confirmed";
}
//# sourceMappingURL=order.js.map